import Button from "@rio-cloud/rio-uikit/Button";
import Select from "@rio-cloud/rio-uikit/Select";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SingleUpBarChart } from "@/charts/chartsComponents/SingleUpBarChart";
import {
    Line,
    SelectContainer,
    SelectContainerNumbers,
    SelectContainers,
    SelectsContainer,
} from "@/charts/chartsComponents/SingleUpBarChartWithToggle.styles";

type SingleUpBarChartWithToggleProps = {
    data: Record<string, unknown>[];
    xAxis: string | null;
};

const SingleUpBarChartWithToggle = ({ data, xAxis }: SingleUpBarChartWithToggleProps) => {
    const intl = useIntl();

    const dialogTitle = intl.formatMessage({ id: 'chatPage.SpeechBubble.toggle.chart.dialog.title' });
    const dialogCloseButtonText = intl.formatMessage({ id: 'chatPage.SpeechBubble.toggle.table.dialog.closeButtonText' });
    const dialogResetButtonText = intl.formatMessage({ id: 'chatPage.SpeechBubble.toggle.table.dialog.resetButtonText' });
    const dialogSearchPlaceholder = intl.formatMessage({ id: 'chatPage.SpeechBubble.toggle.table.dialog.searchPlaceholder' });
    const dialogNotFoundMessage = intl.formatMessage({ id: 'chatPage.SpeechBubble.toggle.table.dialog.notFoundMessage' });

    const dataSize = data.length;
    const defaultCount = dataSize < 10 ? dataSize : 10;

    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

    const metricOptions =
        dataSize > 0
            ? Object.entries(data[0])
                .filter(([key, value]) => key !== xAxis && typeof value === "number" && !isNaN(value))
                .map(([key], index) => ({
                    id: key,
                    label: key,
                    selected: index === 0,
                }))
            : [];

    const [selectedMetric, setSelectedMetric] = useState<string | null>(
        metricOptions[0]?.id || null
    );
    const [filteredData, setFilteredData] = useState<{ name: string | number; value: number }[]>([]);
    const [selectedCount, setSelectedCount] = useState<number>(defaultCount);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);

    const metricNumberOptions = Array.from({ length: Math.floor(dataSize / 5) }, (_, i) => {
        const value = (i + 1) * 5;
        return { id: value.toString(), label: value.toString(), selected: value === defaultCount };
    });

    if (!metricNumberOptions.some((opt) => Number(opt.id) === dataSize)) {
        metricNumberOptions.push({
            id: dataSize.toString(),
            label: intl.formatMessage({ id: "chatPage.text.all" }),
            selected: false,
        });
    }

    const changeColumnOrder = (data: { name: string | number}[]) => {
      const names = data.map((item) => String(item.name).replace(/\s+/g, ""));
      const labels = Object.fromEntries(
        data.map((item) => [
          String(item.name).replace(/\s+/g, ""),
          String(item.name),
        ])
      );
        setColumnOrder(names);
        setColumnLabels(labels);
    };

    useEffect(() => {
        if (selectedMetric && xAxis) {
            const transformed = data.map((item) => ({
                name: (item[xAxis] as string | number) ?? "N/A",
                value: (item[selectedMetric] as number) ?? 0,
            }));

            transformed.sort((a, b) => {
                if (isNaN(a.value) || isNaN(b.value)) {
                    return sortOrder === "asc"
                        ? String(a.name).localeCompare(String(b.name))
                        : String(b.name).localeCompare(String(a.name));
                } else {
                    return sortOrder === "asc" ? a.value - b.value : b.value - a.value;
                }
            });

            const limit = selectedCount >= dataSize ? dataSize : selectedCount;
            const limitedData = transformed.slice(0, limit);

            changeColumnOrder(transformed);
            setFilteredData(limitedData);
        }
    }, [selectedMetric, selectedCount, data, xAxis, sortOrder]);

    const chartWidth = Math.max(filteredData.length * 100, 400);

    const toggleSettingsDialog = () => setShowSettingsDialog((prev) => !prev);

    const entriesNumberLabel = intl
        .formatMessage({ id: "chatPage.SpeechBubble.toggle.chart.entriesNumber.select" })
        .replace("{totalNumber}", dataSize.toString());

    const handleColumnChange = (newColumnOrder: string[], newHiddenColumns: string[]) => {
        setColumnOrder(newColumnOrder);
        setHiddenColumns(newHiddenColumns);
    };

  const orderedVisibleData = columnOrder
    .map((colKey) => {
      const originalName = columnLabels[colKey];
      return filteredData.find((d) => String(d.name) === String(originalName));
    })
    .filter(
      (item): item is { name: string | number; value: number } =>
        !!item &&
        !hiddenColumns.includes(String(item.name).replace(/\s+/g, ""))
    );

    return (
        <div className="margin-top-20">
            <SelectsContainer>
                <SelectContainer>
                    <label htmlFor="metric-select">
                        <FormattedMessage id="chatPage.SpeechBubble.toggle.chart.metric.select" />
                    </label>
                    <Select
                        id="metric-select"
                        placeholder="Select a metric"
                        options={metricOptions}
                        onChange={(item) => setSelectedMetric(item?.id || null)}
                        className="select-box"
                    />
                </SelectContainer>

                <SelectContainerNumbers>
                    <label htmlFor="number-metric-select">{entriesNumberLabel}</label>
                    <Select
                        id="number-metric-select"
                        placeholder="Select a value"
                        options={metricNumberOptions}
                        onChange={(item) => {
                            const num = item?.label === "All" ? dataSize : Number(item?.id);
                            setSelectedCount(num || defaultCount);
                        }}
                        disabled={metricNumberOptions.length === 1}
                        className="select-box select-box-number"
                    />
                </SelectContainerNumbers>

                <SelectContainers>
                    <Line />
                </SelectContainers>

                <SelectContainers>
                    <div className="btn-group">
                        <Button
                            iconOnly
                            iconName="rioglyph-sort-by-attributes-alt"
                            active={sortOrder === "asc"}
                            onClick={() => setSortOrder("asc")}
                        />
                        <Button
                            iconOnly
                            iconName="rioglyph-sort-by-attributes"
                            active={sortOrder === "desc"}
                            onClick={() => setSortOrder("desc")}
                        />
                    </div>
                </SelectContainers>

                <SelectContainers>
                    <div className="table-toolbar-column">
                        <Button iconOnly iconName="rioglyph-settings" onClick={toggleSettingsDialog} />
                    </div>
                </SelectContainers>
            </SelectsContainer>

            <TableSettingsDialog
                show={showSettingsDialog}
                title={dialogTitle}
                onHide={toggleSettingsDialog}
                onColumnChange={handleColumnChange}
                defaultColumnOrder={columnOrder}
                defaultHiddenColumns={hiddenColumns}
                columnOrder={columnOrder}
                hiddenColumns={hiddenColumns}
                columnLabels={columnLabels}
                disabledColumns={[]}
                closeButtonText={dialogCloseButtonText}
                resetButtonText={dialogResetButtonText}
                searchPlaceholder={dialogSearchPlaceholder}
                notFoundMessage={dialogNotFoundMessage}
                autoLabel="Auto"
                onSearchChange={(val: string) => console.log(val)}
                immediateChange
            />

            <SingleUpBarChart width={chartWidth} height={400} data={orderedVisibleData} />
        </div>
    );
};

export default SingleUpBarChartWithToggle;